import * as React from 'react';
import {classNames} from '@shopify/react-utilities/styles';
import {withI18n, WithI18nProps} from 'packages/@shopify/react-i18n';

import {keycodes} from 'utilities';
import {FocusTrap} from 'components/FocusTrap';
import {Icon, Flex, PoweredByShopify} from 'components/UIKit';
import {NOOP} from 'components/UIKit/utilities';
import {NavItemData, SocialLinks} from '../../interfaces';
import {MegaNav, NavDrawerItem, NavSocialLink} from '../../components';
import * as locales from '../../locales';
import styles from './NavDrawer.scss';

const DEFAULT_DRAWER_OPTIONS = {
  ariaLabel: 'Main Navigation',
  role: 'navigation',
};

interface NavDrawerProps {
  open: boolean;
  role?: string;
  ariaLabel?: string;
  wrapperClass?: string;
  openNavs: string[];
  onBack: () => any;
  onCancel: () => any;
  onActiveNavs: (itemPath) => any;
  shopNavItems: NavItemData[];
  infoNavItems: NavItemData[];
  phoneContact: NavItemData;
  callsToAction: NavItemData[];
  socialLinks: SocialLinks[];
}

export class NavDrawerComponent extends React.Component<NavDrawerProps & WithI18nProps> {
  container: any;

  handleContainerKeydown = (event) => {
    if (event.which === keycodes.ESCAPE) {
      const {onCancel = NOOP} = this.props;
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  };

  handleContainerClick = () => {
    const {onCancel = NOOP} = this.props;
    onCancel();
  };

  handleCloseClick = (event) => {
    event.stopPropagation();

    const {onCancel = NOOP} = this.props;
    onCancel();
  };

  handleDrawerClick = (event) => event.stopPropagation();

  render() {
    const {
      open = false,
      ariaLabel = DEFAULT_DRAWER_OPTIONS.ariaLabel,
      role = DEFAULT_DRAWER_OPTIONS.role,
      wrapperClass,
      openNavs,
      onBack = NOOP,
      onActiveNavs = NOOP,
      shopNavItems,
      infoNavItems,
      phoneContact,
      callsToAction,
      socialLinks,
      i18n,
    } = this.props;

    const tabIndex = open ? 0 : -1;
    const hasOpenNestedNav = Boolean(openNavs.length > 0);

    return (
      <FocusTrap active={open}>
        {(focusRef) => (
          <nav
            aria-label={ariaLabel}
            className={classNames(styles.overlay, {
              [styles.open]: open,
              [styles.hasOpenNestedNav]: hasOpenNestedNav,
              [`${wrapperClass}`]: wrapperClass,
            })}
            onClick={this.handleContainerClick}
            onKeyDown={this.handleContainerKeydown}
            ref={focusRef}
            role={role}
          >
            <div className={styles.navDrawer} onClick={this.handleDrawerClick}>
              <div className={styles.scroller}>
                <Flex justifyContent="flex-end" className={styles.header}>
                  <button type="button" className={styles.close} onClick={this.handleCloseClick} tabIndex={tabIndex}>
                    <Icon icon="close" fill="black" size={20} />
                  </button>
                </Flex>

                <ul className={styles.sectionedBottom}>
                  <NavDrawerItem
                    key={phoneContact.internalName}
                    item={phoneContact}
                    tabIndex={tabIndex}
                  />
                </ul>

                <ul className={styles.sectionedBottom}>
                  {callsToAction.map((cta) =>
                    <NavDrawerItem
                      key={cta.internalName}
                      item={cta}
                      tabIndex={tabIndex}
                      tracking={cta.tracking}
                    />)}
                </ul>

                <MegaNav
                  context="drawer"
                  openNavs={openNavs}
                  onBack={onBack}
                  onActiveNavs={onActiveNavs}
                  shopNavItems={shopNavItems}
                  infoNavItems={infoNavItems}
                />

                <div className={classNames(styles.sectionedTop, styles.sectionPadded)}>
                  <Flex>
                    {socialLinks.map((socialItem) =>
                      <NavSocialLink
                        key={socialItem.name}
                        name={socialItem.name}
                        text={i18n.translate(`social.${socialItem.name}`)}
                        url={socialItem.url}
                        tabIndex={tabIndex}
                      />)}
                  </Flex>

                  <PoweredByShopify tabIndex={tabIndex} />
                </div>
              </div>
            </div>
          </nav>
        )}
      </FocusTrap>
    );
  }
}

export const NavDrawer = withI18n({
  id: 'NavDrawer',
  fallback: locales.en,
})(NavDrawerComponent);

export default NavDrawer;
